/**
 * Administración de Mesas y Códigos QR.
 *
 * Permite al dueño o cajero dar de alta mesas, consultar su token firmado de QR,
 * ver el estado actual de la mesa y resetear/cerrar la cuenta.
 */
import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface Mesa {
  id: number;
  sucursal_id: number;
  numero: number;
  nombre: string;
  token_firmado: string;
  capacidad: number;
  estado: 'libre' | 'ocupada' | 'por_cobrar';
  cuenta_activa_id: number | null;
  cuenta_total: number | null;
}

export function GestionMesas({ sucursalId }: { sucursalId: number }) {
  const [mesas, setMesas] = useState<Mesa[]>([]);
  const [cargando, setCargando] = useState(true);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [numero, setNumero] = useState('');
  const [nombre, setNombre] = useState('');
  const [capacidad, setCapacidad] = useState('4');
  const [guardando, setGuardando] = useState(false);
  const [mesaQrSeleccionada, setMesaQrSeleccionada] = useState<Mesa | null>(null);
  const [tokenMostrador, setTokenMostrador] = useState<string | null>(null);
  const [verQrMostrador, setVerQrMostrador] = useState(false);

  useEffect(() => {
    cargarMesas();
    api<{ token: string }>(`/mesas/mostrador-token?sucursalId=${sucursalId}`)
      .then((r) => setTokenMostrador(r.token))
      .catch(() => setTokenMostrador(null));
  }, [sucursalId]);

  async function cargarMesas() {
    try {
      setCargando(true);
      const res = await api<Mesa[]>(`/mesas?sucursalId=${sucursalId}`);
      setMesas(res);
    } catch (err: any) {
      console.error(err);
    } finally {
      setCargando(false);
    }
  }

  async function crearMesa(e: React.FormEvent) {
    e.preventDefault();
    if (!numero) return;

    try {
      setGuardando(true);
      await api('/mesas', {
        method: 'POST',
        body: {
          sucursalId,
          numero: Number(numero),
          nombre: nombre ? nombre.trim() : undefined,
          capacidad: Number(capacidad),
        },
      });

      setNumero('');
      setNombre('');
      setCapacidad('4');
      setMostrarForm(false);
      await cargarMesas();
    } catch (err: any) {
      alert(err?.message || 'Error al crear la mesa.');
    } finally {
      setGuardando(false);
    }
  }

  async function cerrarCuenta(mesa: Mesa) {
    if (!confirm(`¿Cerrar la cuenta de ${mesa.nombre}? La mesa cambiará a estado Libre.`)) return;
    try {
      await api(`/mesas/${mesa.id}/cerrar-cuenta?sucursalId=${sucursalId}`, {
        method: 'POST',
      });
      await cargarMesas();
    } catch (err: any) {
      alert(err?.message || 'Error al cerrar cuenta.');
    }
  }

  const hostBase = window.location.origin;

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-titulo text-2xl font-extrabold text-tinta">Mesas y Códigos QR</h1>
          <p className="text-sm text-salvia">Gestión de mesas del restaurante y enlaces QR para pedidos</p>
        </div>
        <button
          onClick={() => setMostrarForm(!mostrarForm)}
          className="btn-principal self-start text-sm py-2 px-4"
        >
          {mostrarForm ? 'Cancelar' : '+ Nueva Mesa'}
        </button>
      </div>

      {/* QR fijo de mostrador (para llevar) */}
      {tokenMostrador && (
        <div className="mb-6 flex flex-col items-start justify-between gap-3 rounded-2xl border border-nopal/30 bg-nopal-claro p-5 shadow-sm sm:flex-row sm:items-center">
          <div>
            <h3 className="font-titulo text-base font-bold text-nopal-oscuro">🛍️ QR de Mostrador (para llevar)</h3>
            <p className="text-xs text-nopal-oscuro/80">
              Uno solo, fijo, para imprimir y pegar en el mostrador. El cliente escribe su nombre al pedir, así
              que no hace falta un QR distinto por cliente.
            </p>
          </div>
          <button
            onClick={() => setVerQrMostrador(true)}
            className="shrink-0 rounded-lg border border-nopal bg-white px-4 py-2 text-xs font-bold text-nopal-oscuro hover:bg-nopal/10"
          >
            📲 Ver / Imprimir QR
          </button>
        </div>
      )}

      {/* Formulario de Alta */}
      {mostrarForm && (
        <form onSubmit={crearMesa} className="mb-6 rounded-2xl border border-nopal/30 bg-white p-5 shadow-sm">
          <h3 className="font-titulo text-base font-bold text-tinta mb-4">Dar de alta mesa</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-bold text-salvia">Número de Mesa *</label>
              <input
                type="number"
                required
                min="1"
                placeholder="Ej. 1"
                value={numero}
                onChange={(e) => setNumero(e.target.value)}
                className="campo text-base"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-salvia">Nombre (opcional)</label>
              <input
                type="text"
                placeholder="Ej. Terraza 1"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                className="campo text-base"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-salvia">Capacidad (personas)</label>
              <input
                type="number"
                min="1"
                max="30"
                value={capacidad}
                onChange={(e) => setCapacidad(e.target.value)}
                className="campo text-base"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button disabled={guardando} type="submit" className="btn-principal text-sm py-2 px-6">
              {guardando ? 'Guardando…' : 'Guardar Mesa'}
            </button>
          </div>
        </form>
      )}

      {/* Lista de Mesas */}
      {cargando ? (
        <p className="py-12 text-center text-salvia">Cargando mesas…</p>
      ) : mesas.length === 0 ? (
        <div className="rounded-2xl border border-salvia/20 bg-white p-8 text-center text-salvia">
          <p className="text-base font-semibold">No hay mesas registradas en esta sucursal.</p>
          <p className="text-xs mt-1">Haz clic en "+ Nueva Mesa" para dar de alta la primera.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {mesas.map((m) => {
            return (
              <div
                key={m.id}
                className="flex flex-col justify-between rounded-2xl border border-salvia/20 bg-white p-4 shadow-sm"
              >
                <div>
                  <div className="flex items-center justify-between border-b border-salvia/10 pb-2">
                    <span className="font-titulo text-lg font-bold text-tinta">{m.nombre}</span>
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-xs font-bold capitalize ${
                        m.estado === 'libre'
                          ? 'bg-nopal-claro text-nopal-oscuro'
                          : m.estado === 'ocupada'
                          ? 'bg-miel/20 text-miel-oscuro'
                          : 'bg-barro/20 text-barro-oscuro'
                      }`}
                    >
                      {m.estado}
                    </span>
                  </div>

                  <div className="my-3 space-y-1 text-xs text-salvia">
                    <p>Mesa #{m.numero} · Capacidad: {m.capacidad} pers.</p>
                    {m.cuenta_activa_id ? (
                      <p className="font-bold text-nopal-oscuro">
                        Cuenta Abierta: ${Number(m.cuenta_total || 0).toFixed(2)}
                      </p>
                    ) : (
                      <p className="italic">Sin cuenta activa</p>
                    )}
                  </div>
                </div>

                <div className="mt-3 flex flex-col gap-2 pt-2 border-t border-salvia/10">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setMesaQrSeleccionada(m)}
                      className="flex-1 rounded-lg border border-nopal/30 bg-nopal-claro py-2 text-xs font-bold text-nopal-oscuro hover:bg-nopal/20"
                    >
                      📲 Código QR
                    </button>
                    {m.cuenta_activa_id && (
                      <button
                        onClick={() => cerrarCuenta(m)}
                        className="rounded-lg border border-barro/30 bg-barro-claro px-3 py-2 text-xs font-bold text-barro-oscuro hover:bg-barro/20"
                      >
                        Cerrar Cuenta
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de visualización de QR */}
      {mesaQrSeleccionada && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-tinta/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl text-center">
            <h3 className="font-titulo text-xl font-bold text-tinta">
              {mesaQrSeleccionada.nombre}
            </h3>
            <p className="text-xs text-salvia mt-0.5 mb-4">Escanea para pedir desde la mesa</p>

            <div className="mx-auto my-4 flex flex-col items-center justify-center rounded-2xl border-2 border-nopal/30 bg-cal p-4">
              <img
                src={`https://quickchart.io/qr?text=${encodeURIComponent(`${hostBase}/?mesa=${mesaQrSeleccionada.token_firmado}`)}&size=260&margin=1`}
                alt={`QR ${mesaQrSeleccionada.nombre}`}
                className="h-44 w-44 rounded-xl shadow-sm bg-white p-2"
              />
              <p className="mt-2 text-xs font-bold text-nopal-oscuro break-all px-2">
                {`${hostBase}/?mesa=${mesaQrSeleccionada.token_firmado}`}
              </p>
            </div>

            <p className="text-xs text-salvia mb-4">
              Token firmado HMAC-SHA256: <br />
              <code className="font-mono text-[10px] text-tinta">{mesaQrSeleccionada.token_firmado}</code>
            </p>

            <div className="flex gap-2">
              <button
                onClick={() => window.print()}
                className="flex-1 rounded-xl border border-nopal bg-nopal-claro py-2.5 font-bold text-nopal-oscuro hover:bg-nopal/20 text-sm"
              >
                🖨️ Imprimir QR
              </button>
              <button
                onClick={() => setMesaQrSeleccionada(null)}
                className="flex-1 rounded-xl bg-nopal py-2.5 font-bold text-white hover:bg-nopal-oscuro text-sm"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de visualización del QR de mostrador */}
      {verQrMostrador && tokenMostrador && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-tinta/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl text-center">
            <h3 className="font-titulo text-xl font-bold text-tinta">Para llevar</h3>
            <p className="text-xs text-salvia mt-0.5 mb-4">Escanea para pedir en el mostrador, sin mesa</p>

            <div className="mx-auto my-4 flex flex-col items-center justify-center rounded-2xl border-2 border-nopal/30 bg-cal p-4">
              <img
                src={`https://quickchart.io/qr?text=${encodeURIComponent(`${hostBase}/?mostrador=${tokenMostrador}`)}&size=260&margin=1`}
                alt="QR de mostrador"
                className="h-44 w-44 rounded-xl shadow-sm bg-white p-2"
              />
              <p className="mt-2 text-xs font-bold text-nopal-oscuro break-all px-2">
                {`${hostBase}/?mostrador=${tokenMostrador}`}
              </p>
            </div>

            <p className="text-xs text-salvia mb-4">
              Es el mismo QR para todos los clientes: cada quien escribe su nombre al pedir.
            </p>

            <div className="flex gap-2">
              <button
                onClick={() => window.print()}
                className="flex-1 rounded-xl border border-nopal bg-nopal-claro py-2.5 font-bold text-nopal-oscuro hover:bg-nopal/20 text-sm"
              >
                🖨️ Imprimir QR
              </button>
              <button
                onClick={() => setVerQrMostrador(false)}
                className="flex-1 rounded-xl bg-nopal py-2.5 font-bold text-white hover:bg-nopal-oscuro text-sm"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
